import { useEffect } from 'react';
import { ErrorBoundary } from '@/domains/shared/components/ErrorBoundary';
import { RouterProvider, useRouter } from '@/domains/shared/router/Router';
import { DiagnosticProvider } from '@/contexts/DiagnosticContext';
import { AdminAuthProvider, useAdminAuth } from '@/contexts/AdminAuthContext';

import { LandingPage } from '@/pages/public/LandingPage';
import { InstructionsPage } from '@/pages/public/InstructionsPage';
import { QuestionStepperPage } from '@/pages/public/QuestionStepperPage';
import { IdentificationPage } from '@/pages/public/IdentificationPage';
import { ConsentPage } from '@/pages/public/ConsentPage';
import { ProcessingPage } from '@/pages/public/ProcessingPage';
import { ResultPage } from '@/pages/public/ResultPage';
import { ErrorPage } from '@/pages/public/ErrorPage';

import { AdminLoginPage } from '@/pages/admin/AdminLoginPage';
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage';
import { AdminLeadsPage } from '@/pages/admin/AdminLeadsPage';
import { DesignSystemPage } from '@/pages/internal/DesignSystemPage';

function AppRoutes() {
  const { currentPath, navigate } = useRouter();
  const { isAuthenticated, isLoading } = useAdminAuth();

  const isAdminRoute = currentPath === '/admin/dashboard' || currentPath === '/admin/leads';

  // Proteção de rotas administrativas: só redireciona se a checagem de sessão estiver concluída
  useEffect(() => {
    if (isAdminRoute && !isLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAdminRoute, isLoading, isAuthenticated, navigate]);

  // Se estiver restaurando sessão em rota protegida após reload, evita piscar ou redirecionar antes da hora
  if (isAdminRoute && isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy text-brand-offWhite">
        <div className="text-center space-y-3">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
          <p className="text-xs text-brand-offWhite/70 font-medium">
            Verificando credenciais administrativas...
          </p>
        </div>
      </div>
    );
  }

  switch (currentPath) {
    case '/':
      return <LandingPage />;
    case '/instrucoes':
      return <InstructionsPage />;
    case '/diagnostico':
      return <QuestionStepperPage />;
    case '/identificacao':
      return <IdentificationPage />;
    case '/consentimento':
      return <ConsentPage />;
    case '/processando':
      return <ProcessingPage />;
    case '/resultado':
      return <ResultPage />;
    case '/admin/login':
      return <AdminLoginPage />;
    case '/admin/dashboard':
      return isAuthenticated ? <AdminDashboardPage /> : <AdminLoginPage />;
    case '/admin/leads':
      return isAuthenticated ? <AdminLeadsPage /> : <AdminLoginPage />;
    case '/componentes':
      return <DesignSystemPage />;
    case '/erro':
      return <ErrorPage />;
    default:
      // Se a URL contiver /resultado/token, renderiza o resultado
      if (currentPath.startsWith('/resultado')) {
        return <ResultPage />;
      }
      return <LandingPage />;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <RouterProvider>
        <AdminAuthProvider>
          <DiagnosticProvider>
            <AppRoutes />
          </DiagnosticProvider>
        </AdminAuthProvider>
      </RouterProvider>
    </ErrorBoundary>
  );
}
